import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MagnifyingGlassCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import API_URL from '../config/apiConfig';

const QuotasPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [updating, setUpdating] = useState(false);
    const [resetting, setResetting] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            // Récupérer le token d'authentification
            const authToken = JSON.parse(localStorage.getItem('authToken') || '{}');

            const response = await axios.get(`${API_URL}/admin/users`, {
                headers: {
                    Authorization: `Bearer ${authToken.token}`
                }
            });

            if (response.data.success) {
                setUsers(response.data.data.users);
            } else {
                console.error('Erreur lors de la récupération des utilisateurs:', response.data.message);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des utilisateurs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleQuotaChange = async (userId, newQuota) => {
        setUpdating(userId);
        try {
            const authToken = JSON.parse(localStorage.getItem('authToken') || '{}');

            await axios.post(`${API_URL}/admin/adjust-user-quota`, {
                userId,
                newQuota: parseInt(newQuota, 10)
            }, {
                headers: {
                    Authorization: `Bearer ${authToken.token}`
                }
            });

            // Mettre à jour les données localement
            setUsers(users.map(user =>
                user.id === userId
                    ? { ...user, pdfMonthlyQuota: parseInt(newQuota, 10) }
                    : user
            ));
        } catch (error) {
            console.error('Erreur lors de la modification du quota:', error);
        } finally {
            setUpdating(false);
        }
    };

    const handleResetCounter = async (userId) => {
        setResetting(userId);
        try {
            const authToken = JSON.parse(localStorage.getItem('authToken') || '{}');

            await axios.post(`${API_URL}/admin/reset-user-counter`, {
                userId
            }, {
                headers: {
                    Authorization: `Bearer ${authToken.token}`
                }
            });

            // Mettre à jour les données localement
            setUsers(users.map(user =>
                user.id === userId
                    ? { ...user, pdfGenerationCount: 0 }
                    : user
            ));
        } catch (error) {
            console.error('Erreur lors de la réinitialisation du compteur:', error);
        } finally {
            setResetting(false);
        }
    };

    const handleResetAllCounters = async () => {
        if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser tous les compteurs? Cette action ne peut pas être annulée.')) {
            return;
        }

        try {
            const authToken = JSON.parse(localStorage.getItem('authToken') || '{}');

            await axios.post(`${API_URL}/admin/reset-monthly-quotas`, {}, {
                headers: {
                    Authorization: `Bearer ${authToken.token}`
                }
            });

            // Rafraîchir la liste des utilisateurs
            fetchUsers();
        } catch (error) {
            console.error('Erreur lors de la réinitialisation de tous les compteurs:', error);
        }
    };

    // Filtrage par recherche
    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            <div className="flex justify-between mb-6">
                <h1 className="text-2xl font-semibold text-gray-700">Gestion des quotas</h1>
                <button
                    onClick={handleResetAllCounters}
                    className="flex items-center px-4 py-2 bg-[#3D5A80] text-white rounded-md hover:bg-[#2b3f59] focus:outline-none"
                >
                    <ArrowPathIcon className="w-5 h-5 mr-2" />
                    Réinitialiser tous les compteurs
                </button>
            </div>

            {/* Barre de recherche */}
            <div className="relative mb-6">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <MagnifyingGlassCircleIcon className="w-5 h-5 text-gray-400" />
                </div>
                <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Rechercher un utilisateur..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Tableau des utilisateurs */}
            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Utilisateur
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Utilisations / Quota
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ajuster le quota
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                        <tr>
                            <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                                Chargement...
                            </td>
                        </tr>
                    ) : filteredUsers.length > 0 ? (
                        filteredUsers.map((user) => (
                            <tr key={user.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                    <div className="text-sm text-gray-500">{user.email}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                                            <div
                                                className={`h-2.5 rounded-full ${
                                                    (user.pdfGenerationCount / user.pdfMonthlyQuota) > 0.8
                                                        ? 'bg-red-500'
                                                        : (user.pdfGenerationCount / user.pdfMonthlyQuota) > 0.5
                                                            ? 'bg-yellow-500'
                                                            : 'bg-green-500'
                                                }`}
                                                style={{ width: `${Math.min(100, (user.pdfGenerationCount / user.pdfMonthlyQuota) * 100)}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                    <div className="text-sm text-gray-500 mt-1">
                                        {user.pdfGenerationCount} / {user.pdfMonthlyQuota} ({Math.round((user.pdfGenerationCount / user.pdfMonthlyQuota) * 100)}%)
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <input
                                            type="number"
                                            min="0"
                                            value={user.pdfMonthlyQuota}
                                            onChange={(e) => {
                                                const newValue = parseInt(e.target.value);
                                                if (!isNaN(newValue) && newValue >= 0) {
                                                    setUsers(users.map(u =>
                                                        u.id === user.id
                                                            ? { ...u, pdfMonthlyQuota: newValue }
                                                            : u
                                                    ));
                                                }
                                            }}
                                            onBlur={(e) => handleQuotaChange(user.id, e.target.value)}
                                            className="block w-24 px-3 py-1.5 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                        />
                                        {updating === user.id && <span className="ml-2 text-xs text-gray-500">Mise à jour...</span>}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <button
                                        onClick={() => handleResetCounter(user.id)}
                                        disabled={resetting === user.id}
                                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                    >
                                        <ArrowPathIcon className="w-4 h-4 mr-1" />
                                        {resetting === user.id ? 'Réinitialisation...' : 'Réinitialiser compteur'}
                                    </button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                                Aucun utilisateur trouvé
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default QuotasPage;